import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CreditCard, Smartphone, Wallet } from 'lucide-react';
import { API_BASE } from '@/config/api';

declare global {
  interface Window {
    Razorpay: any;
  }
}

const PaymentRazorpay: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'upi' | 'wallet' | 'netbanking'>('card');

  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (!orderId) {
      setError('No order ID provided');
      setIsLoading(false);
      return;
    }

    // Load Razorpay script
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    const initializePayment = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        // Get order details from backend
        const response = await fetch(`${API_BASE}/api/payments/razorpay-order/${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch order details');
        }

        const orderData = await response.json();
        setOrderDetails(orderData);

        // Load Razorpay script
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          throw new Error('Failed to load Razorpay script');
        }

        setIsLoading(false);
      } catch (_err) {
        console.error('Payment initialization error:', err);
        setError(err instanceof Error ? err.message : 'Payment initialization failed');
        setIsLoading(false);
      }
    };

    initializePayment();
  }, [orderId]);

  const handlePayment = () => {
    if (!window.Razorpay || !orderDetails) {
      setError('Razorpay not loaded or order details missing');
      return;
    }

    const options = {
      key: orderDetails.key_id,
      amount: orderDetails.amount,
      currency: orderDetails.currency,
      name: 'MasterJi',
      description: orderDetails.description,
      order_id: orderDetails.order_id,
      prefill: {
        name: orderDetails.customer_name || '',
        email: orderDetails.customer_email || '',
        contact: orderDetails.customer_contact || '',
      },
      theme: {
        color: '#3B82F6',
      },
      handler: function (response: any) {
        console.log('Payment successful:', response);
        // Redirect to success page
        navigate(`/payment-success?session_id=${response.razorpay_order_id}`);
      },
      modal: {
        ondismiss: function() {
          console.log('Payment modal dismissed');
          navigate('/payment-cancelled');
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handleCancel = () => {
    navigate('/payment-cancelled');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Complete Payment</h1>
          <p className="text-gray-600">Secure payment powered by Razorpay</p>
        </div>

        {orderDetails && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">Order Details</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">Plan:</span> {orderDetails.plan_name}</p>
              <p><span className="font-medium">Amount:</span> ₹{(orderDetails.amount / 100).toLocaleString('en-IN')}</p>
              <p><span className="font-medium">Order ID:</span> {orderDetails.order_id}</p>
            </div>
          </div>
        )}

        {/* Payment Method Selection */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Choose Payment Method</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setPaymentMethod('card')}
              className={`p-3 rounded-lg border-2 transition-colors ${
                paymentMethod === 'card'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <CreditCard className="w-6 h-6 mx-auto mb-1" />
              <span className="text-sm font-medium">Card</span>
            </button>
            <button
              onClick={() => setPaymentMethod('upi')}
              className={`p-3 rounded-lg border-2 transition-colors ${
                paymentMethod === 'upi'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Smartphone className="w-6 h-6 mx-auto mb-1" />
              <span className="text-sm font-medium">UPI</span>
            </button>
            <button
              onClick={() => setPaymentMethod('wallet')}
              className={`p-3 rounded-lg border-2 transition-colors ${
                paymentMethod === 'wallet'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Wallet className="w-6 h-6 mx-auto mb-1" />
              <span className="text-sm font-medium">Wallet</span>
            </button>
            <button
              onClick={() => setPaymentMethod('netbanking')}
              className={`p-3 rounded-lg border-2 transition-colors ${
                paymentMethod === 'netbanking'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <CreditCard className="w-6 h-6 mx-auto mb-1" />
              <span className="text-sm font-medium">Net Banking</span>
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={handlePayment}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Pay ₹{orderDetails ? (orderDetails.amount / 100).toLocaleString('en-IN') : '0'}
          </button>
          
          <button
            onClick={handleCancel}
            className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel Payment
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Secured by Razorpay • SSL Encrypted
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentRazorpay;
